import { useCallback, useEffect, useState } from "react";

/**
 * Ventana para borrar (soft delete) una observación: solo el autor y solo
 * durante los primeros 30 minutos desde su creación. Pasada la ventana, la
 * observación queda inmutable desde la UI.
 *
 * ⚠️ Esto es UX, no seguridad: el endpoint update-status hoy acepta el cambio
 * de cualquier usuario autenticado. La misma regla debe validarse en el
 * backend (autor por JWT + antigüedad contra el reloj del servidor) para que
 * sea efectiva.
 */
export const OBSERVATION_DELETE_WINDOW_MS = 30 * 60 * 1000;

/**
 * ¿Puede el usuario actual borrar esta observación?
 * Autor (employeeId) igual al usuario logueado Y creada hace menos de 30 min.
 * Sin autor o sin createdAt parseable, la respuesta es NO (falla cerrado).
 *
 * `createdAt` llega del servidor sin zona horaria ("2026-04-08T14:30:00") y se
 * interpreta como hora local del navegador — si el servidor guardara en otra
 * zona, la ventana se corre: la validación definitiva es la del backend.
 *
 * @param {{employeeId?: number|string, createdAt?: string}} observation
 * @param {number|string|null} currentEmployeeId - empleado logueado
 * @param {number} [now] - epoch ms (inyectable para tests)
 * @returns {boolean}
 */
export function canDeleteObservation(observation, currentEmployeeId, now = Date.now()) {
  const author = Number(observation?.employeeId);
  const me = Number(currentEmployeeId);
  if (!author || !me || author !== me) return false;
  const created = Date.parse(observation?.createdAt);
  if (!Number.isFinite(created)) return false;
  return now - created <= OBSERVATION_DELETE_WINDOW_MS;
}

/**
 * Versión reactiva: devuelve `canDelete(obs)` y re-evalúa cada 30 s, para que
 * el botón de borrar desaparezca solo si el usuario deja la vista abierta
 * hasta que expire la ventana.
 *
 * @param {number|string|null} currentEmployeeId
 * @returns {(observation: Object) => boolean}
 */
export default function useObservationDeleteWindow(currentEmployeeId) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  return useCallback(
    (observation) => canDeleteObservation(observation, currentEmployeeId, now),
    [currentEmployeeId, now]
  );
}
